import React, { useCallback, useEffect, useState } from 'react';

import {
    GetPaperPlanMetadataInput,
    GetPaperPlanMetadataOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import pencilTokens from '@amzn/meridian-tokens/base/icon/pencil';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Tooltip from '@amzn/meridian/tooltip';

import { GridApi } from 'ag-grid-community';
import { Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { Status } from 'src/constants';
import { EDITOR_ROLE_SET, VIEWER_ROLE_SET } from 'src/constants/role';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IBundlePricingRowData } from 'src/types/IBundlePricing';

import { AddBundlePricing } from '../AddBundlePricing';
import { BaseAgGrid } from '../BaseAgGrid';
import { FilterInput } from '../FilterInput';
import { ProtectedRoute } from '../ProtectedRoute';
import './BundlePricing.scss';
import { bundlePricingSortModel, mapPaperPlanMetadataToBundlePricingRowData } from './BundlePricingUtils';
import { bundlePricingColDefs } from './bundlePricingColDefs';

export const BundlePricing: React.FC = () => {
    const { setTopAlertMsg } = React.useContext(AppContext);
    const { path } = useRouteMatch();
    const { pathname } = useLocation();
    const history = useHistory();

    const [filterText, setFilterText] = useState('');
    const [bundlePricingRowData, setBundlePricingRowData] = useState<IBundlePricingRowData[]>([]);
    const [agGridApi, setAgGridApi] = useState<GridApi>();
    const [isDataLoaded, setIsDataLoaded] = useState(false);

    const setDefaultFilter = useCallback(() => {
        const statusFilterModel = {
            status: {
                filterType: 'set',
                values: [Status.Active, Status.Ended, Status.Upcoming, Status.Draft]
            }
        };
        agGridApi?.setFilterModel(statusFilterModel);
    }, [agGridApi]);

    useEffect(() => {
        if (pathname !== ROUTES.BUNDLE_PRICING.INDEX) return;

        contractConfigurationClient
            .getPaperPlanMetadata(
                GetPaperPlanMetadataInput.clone({
                    includeDeleted: false,
                    includeAllAttributeSets: true
                })
            )
            .subscribe(
                (response: GetPaperPlanMetadataOutput) => {
                    setBundlePricingRowData(mapPaperPlanMetadataToBundlePricingRowData(response));
                },
                (error: UnknownCoralError) => {
                    setTopAlertMsg(error.receivedException.message);
                },
                () => setIsDataLoaded(true)
            );
    }, [pathname]);

    return (
        <div className="bundle-pricing-container">
            <Switch>
                <ProtectedRoute exact requiredRoleSet={VIEWER_ROLE_SET} path={path}>
                    <Column className="bundle-pricing-content" spacing="300" width="100%" height="100%" minHeight={480}>
                        <Box type="fill" spacingInset="400">
                            <Row alignmentHorizontal="justify">
                                <FilterInput filterText={filterText} setFilterText={setFilterText} />
                                <Tooltip
                                    position={'bottom'}
                                    title="Editing is disabled in favor of Bundle Price automation. Please contact team for any custom price changes"
                                >
                                    <Button type="link" size="small" disabled data-testid="edit-pricing-btn">
                                        <Icon tokens={pencilTokens} />
                                        Edit Pricing
                                    </Button>
                                </Tooltip>
                            </Row>
                        </Box>
                        <div className="bundle-pricing-grid">
                            <BaseAgGrid
                                columnDefs={bundlePricingColDefs}
                                rowData={bundlePricingRowData}
                                filterText={filterText}
                                sortModel={bundlePricingSortModel}
                                setAgGridApi={setAgGridApi}
                                onFirstDataRendered={setDefaultFilter}
                                loading={!isDataLoaded}
                            />
                        </div>
                    </Column>
                </ProtectedRoute>
                <ProtectedRoute requiredRoleSet={EDITOR_ROLE_SET} path={`${path}${ROUTES.BUNDLE_PRICING.EDIT}`}>
                    <AddBundlePricing />
                </ProtectedRoute>
            </Switch>
        </div>
    );
};
